import { useMemo } from 'react';
import { shouldUseEventDesignDraft } from '@apps/guest/routes/GuestSite/GuestSite.utils';
import { useEventDesignThemingProvider } from '@shared/core/website/theming';
import type { EventPageFragment } from '@graphql/fragments/event.page.graphql.generated';
import { useGuestSiteNavBarQuery } from './guestSiteNavBar.graphql.generated';
import { DesignLayoutType, EventDesignPurpose } from '@graphql/generated-base';
import { isPast } from 'date-fns';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useGetGuestSitePages } from '@shared/hooks/useGetGuestSitePages';
import { useWebsiteV2EventDesign } from '@shared/hooks/useEventWebsiteDesign/useWebsiteV2EventDesign';

interface NavBarControllerProps {
  eventHandle: string;
  layout?: DesignLayoutType;
}

export const useNavBarController = ({ eventHandle, layout }: NavBarControllerProps) => {
  const { data } = useGuestSiteNavBarQuery({
    variables: {
      eventHandle,
      eventDesignPurpose: shouldUseEventDesignDraft() ? EventDesignPurpose.draft : EventDesignPurpose.live,
    },
    batchMode: 'fast',
  });
  const { eventDesign } = useWebsiteV2EventDesign(data?.eventByName?.eventDesign, eventHandle);
  const { pages: guestSitePages } = useGetGuestSitePages({
    pages: data?.eventByName?.pages as EventPageFragment[],
    layoutType: eventDesign?.websiteLayout.layoutType,
  });
  const event = data?.eventByName;
  const info = data?.eventByName?.info;
  const finalizedDateMs = info?.finalizedDate?.milliseconds || 0;
  const isEventPast = isPast(finalizedDateMs);
  const { theme } = useEventDesignThemingProvider();
  const isSmallScreen = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm4' }));
  const navBarVariant: 'menu' | 'inline' = isSmallScreen || layout === DesignLayoutType.aloha ? 'menu' : 'inline';

  const navbarEventProps = useMemo(() => {
    return {
      eventId: event?.id || '',
      layoutType: eventDesign?.websiteLayout.layoutType || DesignLayoutType.aloha,
      dateInMilliseconds: info?.dateV0918?.milliseconds || 0,
      hasEventUnlocked: event?.activeSession.hasUnlocked || false,
      fullNamePerson: event?.activeSession?.person?.fullName || '',
      avatarNamePerson: event?.activeSession?.person?.avatarName || '',
      emailPerson: event?.activeSession?.person?.email || '',
      pages: [...(guestSitePages || [])],
      brannanNavigation: eventDesign?.brannanNavigation,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, eventDesign]);

  return {
    event,
    info,
    isEventPast,
    navBarVariant,
    theme,
    navbarEventProps,
  } as const;
};
